import { axiosCall } from "./httpClient";
import { capitalizeFirstLetter, getCurrentUTCDateTime, INTERACTIONS_BETWEEN_PARTICIPANTS_TYPE, USER_TYPE } from "./constants";
import { BookmarkInterface, ChatMessage, NotesInterface } from "./deepdiveSlice";
import { UID } from "agora-rtc-react";

export const api = {
  verifySession: (lang: string, joiningKey: string, uid: string) =>
    axiosCall({
      url: `/session/verify?lang=${lang}&key=${joiningKey}&uid=${uid}&user_agent=${navigator.userAgent}&is_mobile=false`,
      method: "get",
    }),

  updateJoinee: (
    lang: string,
    sessionId: number,
    sessionMode: number,
    cname: string,
    id: number,
    name: string,
    role: string,
    is_chief_moderator: boolean,
    action: string
  ) => {
    const logs = { action: action, timestamp: getCurrentUTCDateTime() };
    return axiosCall({
      url: `/session/joineee/${id}?lang=${lang}`,
      method: "put",
      data: { sessionId, sessionMode, cname, name, role, is_chief_moderator, logs },
    });
  },

  sendJoinStatus: (
    lang: string,
    sessionId: number,
    userId: string,
    isChiefModerator: boolean,
    name: string,
    joineeId: number,
    joined: boolean
  ) => {
    if (!isChiefModerator && userId === USER_TYPE.MODERATOR) {
      userId = "Co-Facilitator";
    }

    let value = "";
    if (userId === USER_TYPE.RECORDER) {
      value = `Recording ${joined ? "started" : "stopped"}`;
    } else {
      value = `${name} (${capitalizeFirstLetter(userId)}) has ${joined ? "joined" : "left"}`;
    }

    const body = {
      timestamp: Math.floor(Date.now() / 1000),
      connectionID: joineeId,
      value,
      userType: userId,
      userId,
      joined,
    };
    axiosCall({ url: `/session/${sessionId}/joinstatus?lang=${lang}`, method: "put", data: body }).catch(() => {});
  },

  getJoinees: (lang: string, sessionId: number) => axiosCall({ url: `/session/${sessionId}/joinees?lang=${lang}`, method: "get" }),

  allowParticipant: (lang: string, sessionId: number, id: number, role: string, sessionMode: number, cname: string) =>
    axiosCall({ url: `/session/${sessionId}/participant/allow?lang=${lang}`, data: { id, role, sessionMode, cname }, method: "put" }),

  startRecording: (lang: string, sessionId: number, channelName: string, recordingUrl: string, pinnedStreams: UID[]) =>
    axiosCall({
      url: `/session/recording/start?lang=${lang}`,
      data: { sessionId, channelName, recordingUrl, pinnedStreams },
      method: "post",
    }),

  stopRecording: (lang: string, sessionId: number) =>
    axiosCall({ url: `/session/recording/stop?lang=${lang}`, data: { sessionId }, method: "post" }),

  saveChat: (lang: string, sessionId: number, chat: ChatMessage) =>
    axiosCall({ url: `/session/${sessionId}/chat?lang=${lang}`, data: chat, method: "post" }),

  updateRole: (
    lang: string,
    sessionId: number,
    cname: string,
    sessionMode: number,
    uid: number,
    roleToUpdate: string,
    translatorAudioChannel: string | null
  ) =>
    axiosCall({
      url: `/session/${sessionId}/joinee/role?lang=${lang}`,
      data: { cname, sessionMode, uid, roleToUpdate, translatorAudioChannel },
      method: "put",
    }),

  endCall: (lang: string, sessionId: number, mediaSessionKey: string, toConsiderSession: 0 | 1, kickedOut: 0 | 1) =>
    axiosCall({
      url: `/session/${sessionId}/end?lang=${lang}`,
      method: "put",
      data: { mediaSessionKey, to_consider_session: toConsiderSession, kicked_out: kickedOut },
    }),

  saveNote: (lang: string, sessionId: number, notes: NotesInterface) =>
    axiosCall({ url: `/session/${sessionId}/note?lang=${lang}`, data: notes, method: "post" }),

  saveBookmark: (lang: string, sessionId: number, bookmark: BookmarkInterface) =>
    axiosCall({ url: `/session/${sessionId}/bookmark?lang=${lang}`, data: bookmark, method: "post" }),

  updateInteractionBetweenParticipants: (
    lang: string,
    sessionId: number,
    interactionBetweenParticipants: INTERACTIONS_BETWEEN_PARTICIPANTS_TYPE
  ) =>
    axiosCall({
      url: `/session/${sessionId}/interactionBetweenParticipants?lang=${lang}`,
      data: { interactionBetweenParticipants },
      method: "put",
    }),
};
